'use client';

import { followUser, unfollowUser } from '@/src/actions/profile';
import { Spinner, useToast } from '@/src/components/ui';
import { useBoolean } from '@/src/hooks/use-boolean';
import { Button } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib/utils';
import { redirect } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

type Props = {
  creatorId: number;
  isFollow: boolean;
  setIsFollow?: (value: boolean) => void;
  isMyProfile?: boolean;
  isAnonymous?: boolean;
  isMyFollower?: boolean;
  className?: string;
  commitDelayMs?: number;
};

export const Follow = ({
  creatorId,
  isFollow,
  setIsFollow,
  isMyProfile,
  isAnonymous,
  isMyFollower,
  className,
  commitDelayMs = 400,
}: Props) => {
  const toast = useToast();
  const qc = useQueryClient();
  const busy = useBoolean(false);

  const desiredRef = useRef(isFollow);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqIdRef = useRef(0);

  const [isHover, setIsHover] = useState(false);

  useEffect(() => {
    desiredRef.current = isFollow;
  }, [isFollow]);

  const commit = async (finalValue: boolean) => {
    const reqId = ++reqIdRef.current;

    try {
      busy.onTrue();

      if (finalValue) {
        await followUser(creatorId);
      } else {
        await unfollowUser(creatorId);
      }

      if (reqId !== reqIdRef.current) return;

      qc.invalidateQueries({ queryKey: ['followers'] });
      qc.invalidateQueries({ queryKey: ['followings'] });
    } catch (e) {
      if (reqId !== reqIdRef.current) return;

      const rollback = !finalValue;
      setIsFollow?.(rollback);
      desiredRef.current = rollback;

      toast.error('Failed to update follow state.');
    } finally {
      if (reqId === reqIdRef.current) {
        busy.onFalse();
      }
    }
  };

  const scheduleCommit = (finalValue: boolean) => {
    if (timerRef.current) clearTimeout(timerRef.current);

    busy.onTrue();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void commit(finalValue);
    }, commitDelayMs);
  };

  const handleClick = () => {
    if (isAnonymous) redirect('/sign-in');

    const next = !desiredRef.current;
    desiredRef.current = next;
    setIsFollow?.(next);

    scheduleCommit(next);
  };

  const label = isFollow
    ? isHover
      ? 'Unfollow'
      : 'Following'
    : isMyFollower
      ? 'Follow Back'
      : 'Follow';

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleClick}
      disabled={isMyProfile}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      className={cn(
        'w-full bg-[#D9D9D9] disabled:opacity-100 px-2 rounded-sm',
        isFollow && 'bg-white',
        isFollow && isHover && 'hover:border-red-500 hover:text-red-600',
        className,
      )}
    >
      {busy.value ? <Spinner /> : label}
    </Button>
  );
};
